// Order Search — super admin only: find any WMS order, then choose what to create from it.
// The order is the entry point; creation tools (Video Editor, Social Lite, future tools) hang off it.
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, ArrowLeft, Loader2, MapPin, Building2, Video, Film, Clapperboard, History, Pin, PinOff, Images, Sparkles } from "lucide-react";
import { AppHeader } from "@/AppHeader";
import { VideosModal } from "./BranchListings";
import "./VideoSearch.css";

const API = process.env.REACT_APP_BACKEND_URL;
const RECENT_KEY = "video_search_recent_v1";
const PINNED_KEY = "video_search_pinned_v1";
// The demo project (Grant Brooks · Edwards, Ferndown) loads by default; any order can be pinned in its place.
const DEFAULT_DEMO_JOB = "1319921";
const loadRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } };
const loadPinned = () => { try { return localStorage.getItem(PINNED_KEY) || DEFAULT_DEMO_JOB; } catch { return DEFAULT_DEMO_JOB; } };

// Creation tools that can be launched from an order. Add a row here to expose a new tool.
export const CREATION_TOOLS = [
  {
    key: "video", module: "video", name: "Video Editor", tag: "Video footage",
    blurb: "Sneak Peek + reels cut from the photographer's clips. Unchanged.",
    Icon: Clapperboard, needs: (l) => (l.video_products || []).length > 0, needsLabel: "No video product on this order — clips load if the photographer uploaded any",
  },
  {
    key: "social-lite", module: "social-lite", name: "Social Lite", tag: "Photography",
    blurb: "Photo-led Sneak Peek, Just Listed, Price Reduced and Sold edits from the order's Cat 3 photography.",
    Icon: Images, needs: (l) => l.has_photos !== false, needsLabel: "No photography product found on this order",
  },
];

export default function VideoSearch({ onBack, onNavigate }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [listing, setListing] = useState(null);
  const [error, setError] = useState(null);
  const [showVideos, setShowVideos] = useState(false);
  const [recent, setRecent] = useState(loadRecent);
  const [pinned, setPinned] = useState(loadPinned);

  // Open straight onto the pinned demo job so it is one click from a creation tool
  useEffect(() => { if (pinned) search(pinned); }, []); // eslint-disable-line

  const togglePin = () => {
    if (!listing) return;
    const id = String(listing.order_id);
    const next = pinned === id ? DEFAULT_DEMO_JOB : id;
    setPinned(next);
    try { localStorage.setItem(PINNED_KEY, next); } catch {}
    toast.success(pinned === id ? `Unpinned — #${DEFAULT_DEMO_JOB} is the demo again` : `#${id} pinned as your demo job`);
  };

  const search = async (idRaw) => {
    const id = String(idRaw ?? query).replace(/\D/g, "");
    if (!id) { toast.error("Enter a WMS order ID"); return; }
    setLoading(true); setError(null); setListing(null); setShowVideos(false);
    try {
      const r = await axios.get(`${API}/api/listings/lookup/${id}`);
      setListing(r.data);
      setQuery(id);
      const next = [{ id, address: r.data.full_address, client: r.data.client_name }, ...recent.filter((x) => x.id !== id)].slice(0, 6);
      setRecent(next);
      try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
    } catch (err) {
      setError(err.response?.data?.detail || "Could not find that order");
    } finally {
      setLoading(false);
    }
  };

  // Same prefill contract as My Listings → the Video Editor auto-loads the job on mount.
  const openEditor = ({ productFilter, productName, excludeNames, orderedNames } = {}) => {
    try {
      sessionStorage.setItem("prefillWmsJobId", String(listing.order_id));
      const setOrClear = (key, val) => (val ? sessionStorage.setItem(key, val) : sessionStorage.removeItem(key));
      setOrClear("prefillWmsProductFilter", productFilter || "");
      setOrClear("prefillWmsProductLabel", productName || "");
      setOrClear("prefillWmsExcludeNames", excludeNames?.length ? JSON.stringify(excludeNames) : "");
      setOrClear("prefillWmsOrderedNames", orderedNames?.length ? JSON.stringify(orderedNames) : "");
    } catch {}
    onNavigate?.("video");
  };

  // Other tools get the order id via sessionStorage too (Social Lite reads `socialLiteOrderId`).
  const openTool = (tool) => {
    if (tool.key === "video") return openEditor();
    try { sessionStorage.setItem("socialLiteOrderId", String(listing.order_id)); } catch {}
    onNavigate?.(tool.module);
  };

  return (
    <div className="vsx-page" data-testid="order-search-page">
      <AppHeader />
      <div className="vsx-inner">
        <button className="vsx-back" onClick={onBack} data-testid="order-search-back"><ArrowLeft size={16} /> Tools</button>
        <div className="vsx-hero">
          <span className="vsx-eyebrow"><Search size={12} /> Super admin</span>
          <h1 className="vsx-title">Order Search</h1>
          <p className="vsx-sub">Find any WMS order, then choose what to create from it — the Video Editor for footage, Social Lite for photography.</p>
        </div>

        <form className="vsx-search" onSubmit={(e) => { e.preventDefault(); search(); }} data-testid="order-search-form">
          <Search size={18} className="vsx-search-icon" />
          <input
            className="vsx-input"
            inputMode="numeric"
            placeholder="WMS order ID, e.g. 1514334"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="order-search-input"
            autoFocus
          />
          <button type="submit" className="vsx-go" disabled={loading} data-testid="order-search-submit">
            {loading ? <Loader2 size={16} className="vsx-spin" /> : <Search size={16} />} Find order
          </button>
        </form>

        {pinned && (!listing || String(listing.order_id) !== pinned) && !loading && (
          <button className="vsx-pinned" onClick={() => search(pinned)} data-testid="order-search-pinned">
            <Pin size={12} /> Demo job <strong>#{pinned}</strong> — open
          </button>
        )}

        {error && <div className="vsx-error" data-testid="order-search-error">{error}</div>}

        {listing && (
          <div className="vsx-result" data-testid="order-search-result">
            <div className="vsx-result-top">
              <span className={`vsx-status is-${listing.status}`}>{listing.progress_label || listing.status}</span>
              {String(listing.order_id) === pinned && <span className="vsx-demo-tag" data-testid="order-search-demo-tag"><Pin size={11} /> Demo job</span>}
              <span className="vsx-order">#{listing.order_id}</span>
              <button
                className={`vsx-pin ${String(listing.order_id) === pinned ? "is-on" : ""}`}
                onClick={togglePin}
                title={String(listing.order_id) === pinned ? "Unpin (revert to the default demo)" : "Pin as the default demo job"}
                data-testid="order-search-pin"
              >
                {String(listing.order_id) === pinned ? <PinOff size={13} /> : <Pin size={13} />}
              </button>
            </div>
            <h2 className="vsx-address"><MapPin size={16} /> {listing.full_address}</h2>
            <div className="vsx-meta">
              {listing.client_name && <span><Building2 size={13} /> {listing.client_name}</span>}
              {listing.branch_name && <span>{listing.branch_name}</span>}
              {listing.created_at && <span>Ordered {new Date(listing.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>}
            </div>
            <div className="vsx-products">
              {(listing.products || []).map((p) => (
                <span key={p} className={`vsx-pill ${/video|drone|sneak|showreel/i.test(p) ? "is-video" : ""}`}>{/video|drone|sneak|showreel/i.test(p) && <Video size={11} />} {p}</span>
              ))}
            </div>
            <div className="vsx-actions">
              <button className="vsx-btn vsx-btn-ghost" onClick={() => setShowVideos(true)} data-testid="order-search-view-videos"><Film size={15} /> View videos</button>
            </div>

            <div className="vsx-create" data-testid="order-search-create">
              <span className="vsx-create-label"><Sparkles size={12} /> Create from this order</span>
              <div className="vsx-tools">
                {CREATION_TOOLS.map((t) => {
                  const ok = t.needs(listing);
                  return (
                    <button key={t.key} className={`vsx-tool ${ok ? "" : "is-soft"}`} onClick={() => openTool(t)} data-testid={`order-search-open-${t.key}`}>
                      <span className={`vsx-tool-icon is-${t.key}`}><t.Icon size={20} /></span>
                      <span className="vsx-tool-text">
                        <strong>{t.name} <em>{t.tag}</em></strong>
                        <small>{ok ? t.blurb : t.needsLabel}</small>
                      </span>
                      <span className="vsx-tool-go">Open</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {recent.length > 0 && !listing && !loading && (
          <div className="vsx-recent" data-testid="order-search-recent">
            <span className="vsx-recent-label"><History size={12} /> Recent</span>
            {recent.map((r) => (
              <button key={r.id} className="vsx-recent-item" onClick={() => search(r.id)} data-testid={`order-search-recent-${r.id}`}>
                <strong>#{r.id}</strong> <span>{r.address}</span>{r.client && <em>{r.client}</em>}
              </button>
            ))}
          </div>
        )}
      </div>

      {showVideos && listing && (
        <VideosModal listing={listing} onClose={() => setShowVideos(false)} onOpenEditor={openEditor} />
      )}
    </div>
  );
}
